import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getApiBase } from '../../../services/apiBase';
import styles from './CSStudy.module.css';
import datascience from '../../../images/datascience.png';
import fullstack from '../../../images/fullstack.png';
import aiagent from '../../../images/aiagent.png';
import knowledge from '../../../images/knowledge.png';
import DataScienceCanvas from './DataScience/DataScienceCanvas';


const SUBJECT_META = {

  datascience: { flag: datascience, color: '#E91E63', key: 'datascience' },
  fullstack: { flag: fullstack, color: '#1565C0', key: 'fullstack' },
  aiagent: { flag: aiagent, color: '#6A1B9A', key: 'aiagent' },
  'ai-agent': { flag: aiagent, color: '#6A1B9A', key: 'aiagent' },
};

// ─── Subject Configuration & Seed Data ───────────────────────────────────────
const SUBJECT_CONFIG = {
  datascience: {
    id: 'datascience',
    title: 'Data Science',
    icon: '📊',
    categories: ['All', 'Python', 'Pandas & NumPy', 'Machine Learning', 'Data Viz', 'SQL'],
    seeds: [
      {
        id: 'ds-1',
        topic: 'Python List Comprehension',
        summary: 'Compact single-line syntax for filtering and transforming data lists efficiently.',
        detail: `# Traditional loop vs List Comprehension
squares = [x**2 for x in range(10) if x % 2 == 0]
# → [0, 4, 16, 36, 64]

# Flatten 2D Matrix
matrix = [[1, 2], [3, 4]]
flat = [num for row in matrix for num in row]`,
        category: 'Python',
        tags: ['Python', 'Syntax', 'Data Wrangling'],
        created_at: Date.now() - 7 * 86400000,
        review_count: 3,
        ease_factor: 2.5,
      },
      {
        id: 'ds-2',
        topic: 'NumPy Vectorized Operations',
        summary: 'Element-wise array calculations performed at C-speed without Python loop overhead.',
        detail: `import numpy as np

a = np.array([1, 2, 3])
b = np.array([4, 5, 6])

# Fast element-wise addition
c = a + b  # → [5, 7, 9]

# Boolean indexing mask
mask = c > 6
filtered = c[mask]  # → [7, 9]`,
        category: 'Pandas & NumPy',
        tags: ['NumPy', 'Performance', 'Arrays'],
        created_at: Date.now() - 2 * 86400000,
        review_count: 1,
        ease_factor: 2.5,
      },
      {
        id: 'ds-3',
        topic: 'Pandas GroupBy Aggregation',
        summary: 'Split-apply-combine workflow to aggregate tabular datasets by categorical variables.',
        detail: `import pandas as pd

df = pd.DataFrame({
    'Category': ['A', 'A', 'B', 'B'],
    'Sales': [100, 200, 150, 300]
})

grouped = df.groupby('Category')['Sales'].agg(['mean', 'sum'])
print(grouped)`,
        category: 'Pandas & NumPy',
        tags: ['Pandas', 'Aggregation', 'EDA'],
        created_at: Date.now() - 5 * 86400000,
        review_count: 2,
        ease_factor: 2.6,
      },
      {
        id: 'ds-4',
        topic: 'Scikit-Learn Train/Test Split',
        summary: 'Divide dataset into training and validation sets to evaluate model generalization.',
        detail: `from sklearn.model_selection import train_test_split

X_train, X_test, y_train, y_test = train_test_split(
    X, y, test_size=0.2, random_state=42, stratify=y
)
# Ensures test set maintains target class proportions`,
        category: 'Machine Learning',
        tags: ['Scikit-Learn', 'ML', 'Validation'],
        created_at: Date.now() - 1 * 86400000,
        review_count: 0,
        ease_factor: 2.5,
      },
      {
        id: 'ds-5',
        topic: 'Matplotlib Correlation Heatmap',
        summary: 'Visualize feature correlation matrices with color-coded Pearson coefficients.',
        detail: `import seaborn as sns
import matplotlib.pyplot as plt

corr = df.corr()
sns.heatmap(corr, annot=True, cmap='coolwarm', vmin=-1, vmax=1)
plt.title('Feature Correlation Matrix')
plt.show()`,
        category: 'Data Viz',
        tags: ['Visualization', 'Seaborn', 'Heatmap'],
        created_at: Date.now() - 3 * 86400000,
        review_count: 4,
        ease_factor: 2.7,
      },
      {
        id: 'ds-6',
        topic: 'SQL Window Functions (DENSE_RANK)',
        summary: 'Calculate rankings and running statistics over partitioned window frames.',
        detail: `SELECT 
    department_id,
    employee_name,
    salary,
    DENSE_RANK() OVER (
        PARTITION BY department_id 
        ORDER BY salary DESC
    ) as salary_rank
FROM employees;`,
        category: 'SQL',
        tags: ['SQL', 'Window Function', 'Database'],
        created_at: Date.now() - 10 * 86400000,
        review_count: 5,
        ease_factor: 2.8,
      },
    ]
  },
  fullstack: {
    id: 'fullstack',
    title: 'FullStack Web Dev',
    icon: '💻',
    categories: ['All', 'React', 'Frontend', 'Node & Express', 'CSS', 'APIs'],
    seeds: [
      {
        id: 'fs-1',
        topic: 'CSS Flexbox Centering',
        summary: 'Horizontal and vertical alignment using justify-content and align-items with zero hacks.',
        detail: `.parent-container {
  display: flex;
  justify-content: center; /* Horizontal alignment */
  align-items: center;     /* Vertical alignment */
  height: 100vh;
}`,
        category: 'CSS',
        tags: ['CSS', 'Flexbox', 'Layout'],
        created_at: Date.now() - 4 * 86400000,
        review_count: 2,
        ease_factor: 2.5,
      },
      {
        id: 'fs-2',
        topic: 'React useState & useEffect Hooks',
        summary: 'State management and side-effect lifecycle handling in modern functional components.',
        detail: `import { useState, useEffect } from 'react';

function UserProfile({ userId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(\`/api/users/\${userId}\`)
      .then(res => res.json())
      .then(setData);
  }, [userId]); // Triggers when userId changes

  return <div>{data ? data.name : 'Loading...'}</div>;
}`,
        category: 'React',
        tags: ['React', 'Hooks', 'Frontend'],
        created_at: Date.now() - 1 * 86400000,
        review_count: 0,
        ease_factor: 2.5,
      },
      {
        id: 'fs-3',
        topic: 'Express.js Middleware Pipeline',
        summary: 'Sequentially process HTTP requests, inspect headers, handle auth, and route responses.',
        detail: `const authMiddleware = (req, res, next) => {
  const token = req.headers['authorization'];
  if (!token) return res.status(401).json({ error: 'Unauthorized' });
  req.user = verifyToken(token);
  next(); // Pass to next handler
};

app.get('/api/protected', authMiddleware, (req, res) => {
  res.json({ user: req.user });
});`,
        category: 'Node & Express',
        tags: ['Node.js', 'Express', 'Backend'],
        created_at: Date.now() - 6 * 86400000,
        review_count: 3,
        ease_factor: 2.6,
      },
      {
        id: 'fs-4',
        topic: 'RESTful API Endpoint Design',
        summary: 'Standardized URI endpoint naming utilizing HTTP verbs (GET, POST, PUT, DELETE).',
        detail: `GET    /api/v1/posts        -> Fetch all posts
POST   /api/v1/posts        -> Create a new post
GET    /api/v1/posts/:id    -> Fetch post by ID
PUT    /api/v1/posts/:id    -> Update post by ID
DELETE /api/v1/posts/:id    -> Delete post by ID`,
        category: 'APIs',
        tags: ['REST', 'API', 'Architecture'],
        created_at: Date.now() - 8 * 86400000,
        review_count: 4,
        ease_factor: 2.7,
      },
      {
        id: 'fs-5',
        topic: 'CSS Grid Responsive Layout (minmax)',
        summary: 'Fluid multi-column grid layout that automatically adjusts without media queries.',
        detail: `.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}`,
        category: 'CSS',
        tags: ['CSS', 'Grid', 'Responsive'],
        created_at: Date.now() - 2 * 86400000,
        review_count: 1,
        ease_factor: 2.5,
      },
      {
        id: 'fs-6',
        topic: 'Async/Await Error Handling',
        summary: 'Write asynchronous JavaScript code with synchronous readability and error boundary safety.',
        detail: `async function fetchUserData(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(\`HTTP status \${response.status}\`);
    const data = await response.json();
    return data;
  } catch (err) {
    console.error('Fetch failed:', err.message);
    throw err;
  }
}`,
        category: 'Frontend',
        tags: ['JavaScript', 'Async', 'Promises'],
        created_at: Date.now() - 5 * 86400000,
        review_count: 2,
        ease_factor: 2.6,
      },
    ]
  },
  'ai-agent': {
    id: 'ai-agent',
    title: 'AI & Autonomous Agents',
    icon: '🤖',
    categories: ['All', 'Prompt Engineering', 'Agent Loop', 'RAG & Vector', 'Tool Calling'],
    seeds: [
      {
        id: 'ai-1',
        topic: 'System Prompt Role Specification',
        summary: 'Guide LLM output tone, boundary constraints, and reasoning steps through explicit persona instructions.',
        detail: `System Prompt Structure:
1. Role Definition: "You are an expert CS tutor..."
2. Task Goal: "Extract key flashcard points from input."
3. Constraints: "Output strictly JSON in English."
4. Few-shot Examples: Provide input/output sample pairs.`,
        category: 'Prompt Engineering',
        tags: ['LLMs', 'Prompts', 'System Role'],
        created_at: Date.now() - 3 * 86400000,
        review_count: 1,
        ease_factor: 2.5,
      },
      {
        id: 'ai-2',
        topic: 'ReAct Agent Loop Pattern (Reason + Act)',
        summary: 'Interleave Thought, Action, and Observation cycles to break down multi-step autonomous tasks.',
        detail: `Thought: The user asks for current weather in Sydney.
Action: call_tool("get_weather", { city: "Sydney" })
Observation: { temp: "22C", condition: "Sunny" }
Thought: Now I have the weather information to answer the user.
Final Answer: The current weather in Sydney is sunny and 22°C.`,
        category: 'Agent Loop',
        tags: ['ReAct', 'Agent Architecture', 'Autonomous'],
        created_at: Date.now() - 1 * 86400000,
        review_count: 0,
        ease_factor: 2.5,
      },
      {
        id: 'ai-3',
        topic: 'Vector Embeddings & Cosine Similarity',
        summary: 'Transform text into dense numerical vectors to measure semantic proximity in vector space.',
        detail: `// Cosine Similarity Formula
similarity = (A • B) / (||A|| * ||B||)

// 1.0 = identical semantic meaning
// 0.0 = completely orthogonal / unrelated
// Used in semantic search, chunk retrieval, and RAG pipelines.`,
        category: 'RAG & Vector',
        tags: ['Vector DB', 'Embeddings', 'Math'],
        created_at: Date.now() - 5 * 86400000,
        review_count: 3,
        ease_factor: 2.6,
      },
      {
        id: 'ai-4',
        topic: 'RAG Pipeline (Retrieval-Augmented Generation)',
        summary: 'Enhance LLM accuracy by retrieving external vector context before generating responses.',
        detail: `RAG Workflow Steps:
1. Ingest & Chunk document text
2. Generate embeddings & index in Vector DB (Pinecone/Milvus/Supabase)
3. Query Vector DB with user input embedding
4. Inject top-k matching chunks into LLM prompt context
5. LLM generates grounded answer without hallucination`,
        category: 'RAG & Vector',
        tags: ['RAG', 'Vector Search', 'Knowledge Retrieval'],
        created_at: Date.now() - 7 * 86400000,
        review_count: 4,
        ease_factor: 2.7,
      },
      {
        id: 'ai-5',
        topic: 'Structured Tool / Function Calling',
        summary: 'Enable LLMs to return validated JSON schema calls to execute external APIs and database queries.',
        detail: `// OpenAI / Gemini Tool Definition
{
  name: "search_web",
  description: "Search live web for queries",
  parameters: {
    type: "OBJECT",
    properties: {
      query: { type: "STRING" }
    },
    required: ["query"]
  }
}`,
        category: 'Tool Calling',
        tags: ['Function Calling', 'APIs', 'Tools'],
        created_at: Date.now() - 2 * 86400000,
        review_count: 2,
        ease_factor: 2.5,
      },
      {
        id: 'ai-6',
        topic: 'Context Window & Sliding Truncation',
        summary: 'Manage conversation token limits by summarizing older messages while preserving active state.',
        detail: `Strategy for Long Conversations:
- Keep System Prompt + last 5 dialogue turns
- Summarize turns 1..N-5 into a single summary block
- Prevents context overflow & reduces token costs`,
        category: 'Prompt Engineering',
        tags: ['Tokens', 'Context Window', 'Optimization'],
        created_at: Date.now() - 9 * 86400000,
        review_count: 5,
        ease_factor: 2.8,
      },
    ]
  }
};

// ─── Spaced Repetition Algorithm ─────────────────────────────────────────────
function getReviewPool(allCards, count = 5) {
  const now = Date.now();

  const scored = allCards.map(card => {
    const ageDays = (now - (card.created_at || now)) / 86400000;
    const nextReview = ageDays * (card.ease_factor || 2.5) / Math.max(1, card.review_count || 1);

    let weight;
    if ((card.review_count || 0) === 0) {
      weight = 'new';
    } else if (nextReview <= 1) {
      weight = 'critical';
    } else {
      weight = 'spaced';
    }

    return { ...card, _weight: weight, _score: nextReview };
  });

  const critical = scored.filter(c => c._weight === 'critical').sort(() => Math.random() - 0.5);
  const newCards = scored.filter(c => c._weight === 'new').sort(() => Math.random() - 0.5);
  const spaced = scored.filter(c => c._weight === 'spaced').sort(() => Math.random() - 0.5);

  const criticalCount = Math.ceil(count * 0.4);
  const newCount = Math.ceil(count * 0.4);
  const spacedCount = count - criticalCount - newCount;

  const pool = [
    ...critical.slice(0, criticalCount),
    ...newCards.slice(0, newCount),
    ...spaced.slice(0, spacedCount),
  ];

  if (pool.length < count) {
    const used = new Set(pool.map(c => c.id));
    const remaining = scored.filter(c => !used.has(c.id)).sort(() => Math.random() - 0.5);
    pool.push(...remaining.slice(0, count - pool.length));
  }

  return pool.slice(0, count).sort(() => Math.random() - 0.5);
}

// ─── Particle Sparkle Effect ──────────────────────────────────────────────────
function triggerSparkle(x, y) {
  const colors = ['#4CAF50', '#81C784', '#A5D6A7', '#FFEB3B', '#FFF9C4'];
  for (let i = 0; i < 16; i++) {
    const p = document.createElement('div');
    p.className = styles.sparkleParticle;
    const angle = (i / 16) * Math.PI * 2;
    const distance = 40 + Math.random() * 60;
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance;
    const size = 4 + Math.random() * 6;
    p.style.cssText = `
      left:${x}px; top:${y}px;
      width:${size}px; height:${size}px;
      background:${colors[Math.floor(Math.random() * colors.length)]};
      --dx:${dx}px; --dy:${dy}px;
    `;
    document.body.appendChild(p);
    setTimeout(() => p.remove(), 800);
  }
}

// ─── Floating card motion presets ─────────────────────────────────────────────
const FLOAT_PRESETS = [
  { x1: 0, y1: 0, x2: 16, y2: -18, x3: -10, y3: 12, dur: 18 },
  { x1: 0, y1: 0, x2: -18, y2: 12, x3: 8, y3: -15, dur: 22 },
  { x1: 0, y1: 0, x2: 12, y2: 16, x3: -6, y3: -10, dur: 16 },
  { x1: 0, y1: 0, x2: -14, y2: -15, x3: 10, y3: 8, dur: 25 },
  { x1: 0, y1: 0, x2: 18, y2: 6, x3: -12, y3: -14, dur: 20 },
  { x1: 0, y1: 0, x2: -8, y2: 18, x3: 15, y3: -6, dur: 24 },
];

export default function CSStudy() {
  const navigate = useNavigate();
  const { subject } = useParams();

  // Default to 'datascience' if invalid subject param
  const activeSubject = SUBJECT_CONFIG[subject] || SUBJECT_CONFIG.datascience;
  const meta = SUBJECT_META[subject] || SUBJECT_META[activeSubject.id] || SUBJECT_META.datascience;
  const storageKey = `cs-cards-${activeSubject.id}`;

  const [allCards, setAllCards] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const storedIds = new Set(parsed.map(c => c.id));
        return [
          ...activeSubject.seeds.filter(c => !storedIds.has(c.id)),
          ...parsed,
        ];
      }
    } catch (_) { }
    return activeSubject.seeds;
  });

  // Re-sync cards when subject param changes
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const storedIds = new Set(parsed.map(c => c.id));
        setAllCards([
          ...activeSubject.seeds.filter(c => !storedIds.has(c.id)),
          ...parsed,
        ]);
        return;
      }
    } catch (_) { }
    setAllCards(activeSubject.seeds);
  }, [storageKey, activeSubject]);

  const [floatingPool, setFloatingPool] = useState([]);
  const [paused, setPaused] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [search, setSearch] = useState('');
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureInput, setCaptureInput] = useState('');
  const [captureLoading, setCaptureLoading] = useState(false);
  const [capturePreview, setCapturePreview] = useState(null);
  const [captureError, setCaptureError] = useState('');
  const captureInputRef = useRef(null);

  // Counter animation
  useEffect(() => {
    const target = allCards.length;
    let current = 0;
    const step = Math.ceil(target / 25);
    const timer = setInterval(() => {
      current = Math.min(current + step, target);
      if (current >= target) clearInterval(timer);
    }, 35);
    return () => clearInterval(timer);
  }, [allCards.length]);

  // Persist cards to localStorage per subject
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(allCards));
  }, [allCards, storageKey]);

  // Init floating pool
  const refreshPool = useCallback(() => {
    setFloatingPool(getReviewPool(allCards, 6));
  }, [allCards]);

  useEffect(() => { refreshPool(); }, [refreshPool]);

  // Focus input when capture opens
  useEffect(() => {
    if (captureOpen) setTimeout(() => captureInputRef.current?.focus(), 100);
  }, [captureOpen]);



  // ── AI Card Generation ─────────────────────────────────────────────────────
  const handleGenerateCard = async () => {
    if (!captureInput.trim()) return;
    setCaptureLoading(true);
    setCaptureError('');
    setCapturePreview(null);
    try {
      const API = axios.create({ baseURL: getApiBase(), timeout: 30000 });
      const { data } = await API.post('/api/cs/generate-card', {
        input: captureInput,
        subject: activeSubject.title,
      });
      setCapturePreview({
        ...data,
        id: `card-${Date.now()}`,
        created_at: Date.now(),
        review_count: 0,
        ease_factor: 2.5,
      });
    } catch (err) {
      setCaptureError(err.response?.data?.error || 'AI generation failed. Please try again.');
    } finally {
      setCaptureLoading(false);
    }
  };

  // ── Confirm card into knowledge base ──────────────────────────────────────
  const handleConfirmCard = useCallback((e) => {
    if (!capturePreview) return;
    const rect = e.currentTarget.getBoundingClientRect();
    triggerSparkle(rect.left + rect.width / 2, rect.top + rect.height / 2);

    setCapturePreview(null);
    setCaptureInput('');
    setCaptureOpen(false);
    setTimeout(() => setFloatingPool(pool => [capturePreview, ...pool.slice(0, 5)]), 200);
  }, [capturePreview]);

  // ── Card click ───────────────────────────────────────────────────────────
  const handleCardClick = useCallback((card) => {
    setSelectedCard(card);
    setAllCards(prev => prev.map(c =>
      c.id === card.id
        ? { ...c, review_count: (c.review_count || 0) + 1 }
        : c
    ));
  }, []);

  // ── Delete card ───────────────────────────────────────────────────────────
  const handleDelete = useCallback((id) => {
    setAllCards(prev => prev.filter(c => c.id !== id));
    setFloatingPool(prev => prev.filter(c => c.id !== id));
    if (selectedCard?.id === id) setSelectedCard(null);
  }, [selectedCard]);

  // ── Tag color helper ──────────────────────────────────────────────────────
  const tagColor = (tag) => {
    const map = {
      Python: '#4CAF50', NumPy: '#42A5F5', Pandas: '#AB47BC', SQL: '#78909C',
      CSS: '#FF7043', Flexbox: '#FF7043', Grid: '#FF7043', Layout: '#FF7043',
      React: '#29B6F6', Hooks: '#26C6DA', JavaScript: '#FFA726',
      'Node.js': '#66BB6A', Express: '#5C6BC0', REST: '#8D6E63', API: '#8D6E63',
      LLMs: '#9C27B0', Prompts: '#AB47BC', ReAct: '#EF5350', RAG: '#26A69A',
      'Vector DB': '#42A5F5', Embeddings: '#7E57C2', Tools: '#FFA726',
    };
    return map[tag] || '#78909C';
  };

  // ── Shared A4 Canvas ───────────────────────────────────────────────────
  // Each CS path keeps its own cards via `storageKey`, while exposing the
  // same canvas, AI capture, PDF export, and password-protected editing flow.
  if (['datascience', 'fullstack', 'ai-agent'].includes(activeSubject.id)) {
    return (
      <div className={styles.page}>
        {/* Compact header (shared) */}
        <header className={styles.header}>
          <h1 className={styles.pageTitle}>
            {meta?.flag && (
              <img
                src={meta.flag}
                alt={meta.key || activeSubject.title}
                className={styles.headerLogo}
              />
            )}
            {activeSubject.title}
          </h1>
          <button className={styles.operationPasswordBtn} onClick={() => window.dispatchEvent(new CustomEvent('open-cs-operation-password'))}>
            Password
          </button>
          <button className={styles.backBtn} onClick={() => navigate('/study/cs')}>
            ← Back
          </button>
        </header>

        {/* A4 Canvas system */}
        <DataScienceCanvas
          allCards={allCards}
          setAllCards={setAllCards}
          activeSubject={activeSubject}
          storageKey={storageKey}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* ── Compact Header ───────────────────────────────────────────────── */}
      <header className={styles.header}>
        <h1 className={styles.pageTitle}>
          {meta?.flag && (
            <img
              src={meta.flag}
              alt={meta.key || activeSubject.title}
              className={styles.headerLogo}
            />
          )}
          {activeSubject.title}
        </h1>
        <button className={styles.backBtn} onClick={() => navigate('/study/cs')}>
          Back
        </button>
      </header>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/*  SECTION 1: Dynamic Floating Canvas                               */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section className={styles.floatingSection}>
        {/* Background particles */}
        <div className={styles.particleBg} aria-hidden="true">
          {[...Array(18)].map((_, i) => (
            <div key={i} className={styles.bgParticle} style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${6 + Math.random() * 8}s`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              opacity: 0.12 + Math.random() * 0.2,
            }} />
          ))}
        </div>

        {/* Controls */}
        <div className={styles.floatingControls}>
          <button className={styles.ctrlBtn} onClick={refreshPool}>
            - Refresh Pool
          </button>
          <button
            className={`${styles.ctrlBtn} ${paused ? styles.ctrlBtnActive : ''}`}
            onClick={() => setPaused(p => !p)}
          >
            {paused ? '▶ Resume Float' : '⏸ Pause Float'}
          </button>
        </div>

        {/* Floating cards */}
        <div className={styles.floatingArena}>
          {floatingPool.map((card, i) => {
            const preset = FLOAT_PRESETS[i % FLOAT_PRESETS.length];
            const delay = i * 1.1;
            return (
              <div
                key={card.id + '-float-' + i}
                className={`${styles.floatCard} ${paused ? styles.floatCardPaused : ''}`}
                style={{
                  '--x1': `${preset.x1}px`, '--y1': `${preset.y1}px`,
                  '--x2': `${preset.x2}px`, '--y2': `${preset.y2}px`,
                  '--x3': `${preset.x3}px`, '--y3': `${preset.y3}px`,
                  '--dur': `${preset.dur}s`,
                  animationDelay: `-${delay}s`,
                  left: `${8 + (i % 3) * 29 + (i > 2 ? 4 : 0)}%`,
                  top: i < 3 ? '12%' : '50%',
                }}
                onClick={() => handleCardClick(card)}
              >
                <div className={styles.floatCardTopic}>{card.topic}</div>
                <div className={styles.floatCardSummary}>{card.summary}</div>
                <div className={styles.floatCardHint}>Click for detail →</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/*  SECTION 2: Knowledge Hub Grid                                    */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section className={styles.hubSection}>
        <div className={styles.hubHeader}>
          <div className={styles.hubTitleWrap}>
            <img src={knowledge} alt="knowledge" width={26} height={26} />
            <h2 className={styles.hubTitle}> Knowledge Base</h2>
          </div>

          <div className={styles.hubHeaderRight}>
            <input
              className={styles.searchInput}
              type="text"
              placeholder="🔍 Search flashcards, tags..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />

            {/* AI Quick Capture Widget */}
            <div className={styles.captureWidgetWrap}>
              <button
                className={`${styles.captureBtn} ${captureOpen ? styles.captureBtnOpen : ''}`}
                onClick={() => setCaptureOpen(o => !o)}
                title="Quick Note"
              >
                {captureOpen ? '✕' : 'Quick Note (AI)'}
              </button>

              {captureOpen && (
                <div className={styles.capturePanel}>
                  <div className={styles.capturePanelHeader}>
                    <span>Quick Note (AI)</span>
                    <button className={styles.captureClose} onClick={() => { setCaptureOpen(false); setCapturePreview(null); setCaptureError(''); }}>✕</button>
                  </div>

                  {!capturePreview ? (
                    <>
                      <input
                        ref={captureInputRef}
                        className={styles.captureInput}
                        type="text"
                        placeholder="What did you learn? (e.g. learned numpy array slicing today)"
                        value={captureInput}
                        onChange={e => setCaptureInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !captureLoading && handleGenerateCard()}
                        disabled={captureLoading}
                      />
                      {captureError && <p className={styles.captureError}>{captureError}</p>}
                      <button
                        className={`${styles.captureSubmit} ${captureLoading ? styles.captureSubmitLoading : ''}`}
                        onClick={handleGenerateCard}
                        disabled={captureLoading || !captureInput.trim()}
                      >
                        {captureLoading ? (
                          <><span className={styles.spinner} /> AI Extracting...</>
                        ) : '✨ AI Generate Card'}
                      </button>
                    </>
                  ) : (
                    <div className={styles.previewCard}>
                      <div className={styles.previewLabel}>✅ Card Generated</div>
                      <div className={styles.previewTopic}>{capturePreview.topic}</div>
                      <div className={styles.previewSummary}>{capturePreview.summary}</div>
                      <div className={styles.previewTags}>
                        {(capturePreview.tags || []).map(t => (
                          <span key={t} className={styles.tag} style={{ background: tagColor(t) + '22', color: tagColor(t), borderColor: tagColor(t) + '44' }}>{t}</span>
                        ))}
                      </div>
                      <div className={styles.previewActions}>
                        <button className={styles.previewRedo} onClick={() => setCapturePreview(null)}>← Retry</button>
                        <button className={styles.previewConfirm} onClick={handleConfirmCard}>🌱 Save Card</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compact Card Grid */}
        <div className={styles.cardGrid}>
          {allCards.length === 0 && (
            <div className={styles.emptyState}>
              <span>🌱</span>
              <p>No flashcards found for {activeSubject.title}. Add one using the Quick Note widget above!</p>
            </div>
          )}
          {allCards.map((card, i) => (
            <div
              key={card.id}
              className={styles.hubCard}
              style={{ animationDelay: `${i * 0.04}s` }}
              onClick={() => handleCardClick(card)}
            >
              <div className={styles.hubCardHeader}>
                <button
                  className={styles.deleteBtn}
                  onClick={e => { e.stopPropagation(); handleDelete(card.id); }}
                  title="Delete card"
                >✕</button>
              </div>
              <h3 className={styles.hubCardTopic}>{card.topic}</h3>
              <p className={styles.hubCardSummary}>{card.summary}</p>
              <div className={styles.hubCardFooter}>
                <span className={styles.hubCardReview}>
                  Reviewed {card.review_count || 0} times
                </span>
                <span className={styles.hubCardDate}>
                  {new Date(card.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/*  MODAL: Card Detail                                                */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {selectedCard && (
        <div className={styles.modalOverlay} onClick={() => setSelectedCard(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setSelectedCard(null)}>✕</button>
            <div className={styles.modalTags}>
              {(selectedCard.tags || []).map(t => (
                <span key={t} className={styles.tag} style={{ background: tagColor(t) + '22', color: tagColor(t), borderColor: tagColor(t) + '44' }}>{t}</span>
              ))}
            </div>
            <h2 className={styles.modalTopic}>{selectedCard.topic}</h2>
            <p className={styles.modalSummary}>{selectedCard.summary}</p>
            <div className={styles.modalDivider} />
            <pre className={styles.modalDetail}>{selectedCard.detail}</pre>
            <div className={styles.modalFooter}>
              <span>🔁 Reviewed {selectedCard.review_count || 0} times</span>
              <span className={styles.modalCategory}>{selectedCard.category}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
